// services/PrintService.js
import QRCode from 'qrcode';

/**
 * Print Service - Professional Busy Accounting Software Style
 * Complete invoice printing solution with GST compliance
 */

class PrintService {
  constructor() {
    // Professional paper size configurations
    this.paperSizes = {
      a4: {
        name: 'A4',
        width: 210,
        height: 297,
        unit: 'mm',
        margin: '10mm',
        css: `
          @page { 
            size: A4; 
            margin: 10mm;
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            width: 100%;
            margin: 0 auto;
            font-size: 10pt;
            line-height: 1.4;
            color: #000000;
          }
          .invoice-wrapper {
            max-width: 100%;
            margin: 0 auto;
            background: #ffffff;
          }
        `
      },
      a5: {
        name: 'A5',
        width: 148,
        height: 210,
        unit: 'mm',
        margin: '8mm',
        css: `
          @page { 
            size: A5; 
            margin: 8mm; 
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            width: 100%;
            margin: 0 auto;
            font-size: 9pt;
            line-height: 1.3;
          }
          .invoice-wrapper {
            max-width: 100%;
            margin: 0 auto;
          }
        `
      },
      'thermal-80': {
        name: 'Thermal 80mm',
        width: 80,
        height: 'auto',
        unit: 'mm',
        margin: '2mm',
        css: `
          @page { 
            size: 80mm auto; 
            margin: 2mm;
          }
          body { 
            font-family: 'Courier New', monospace;
            width: 76mm;
            margin: 0 auto;
            font-size: 9pt;
            line-height: 1.2;
          }
          .thermal-invoice {
            width: 76mm;
            margin: 0 auto;
          }
          table { 
            width: 100%;
            border-collapse: collapse;
          }
        `,
        thermal: true
      },
      'thermal-58': {
        name: 'Thermal 58mm',
        width: 58,
        height: 'auto',
        unit: 'mm',
        margin: '2mm',
        css: `
          @page { 
            size: 58mm auto; 
            margin: 2mm;
          }
          body { 
            font-family: 'Courier New', monospace;
            width: 54mm;
            margin: 0 auto;
            font-size: 8pt;
            line-height: 1.2;
          }
          .thermal-invoice {
            width: 54mm;
            margin: 0 auto;
          }
        `,
        thermal: true
      },
      letter: {
        name: 'Letter',
        width: 216,
        height: 279,
        unit: 'mm',
        margin: '0.5in',
        css: `
          @page { 
            size: Letter; 
            margin: 0.5in; 
          }
          body { 
            font-family: 'Arial', 'Helvetica', sans-serif;
            width: 100%;
            margin: 0 auto;
            font-size: 10pt;
          }
        `
      }
    };

    // Professional bill formats
    this.billFormats = {
      'tax-invoice': {
        title: 'TAX INVOICE',
        subtitle: 'Under GST Act 2017',
        showGstDetails: true
      },
      'cash-memo': {
        title: 'CASH MEMO',
        subtitle: 'Original for Buyer',
        showGstDetails: true
      },
      'proforma': {
        title: 'PROFORMA INVOICE',
        subtitle: 'Subject to confirmation',
        showGstDetails: false
      },
      'delivery-challan': {
        title: 'DELIVERY CHALLAN',
        subtitle: 'Goods sent on approval basis',
        showGstDetails: false
      },
      'credit-note': {
        title: 'CREDIT NOTE',
        subtitle: 'Under GST Act 2017',
        showGstDetails: true
      },
      'debit-note': {
        title: 'DEBIT NOTE',
        subtitle: 'Under GST Act 2017',
        showGstDetails: true
      }
    };
  }

  /**
   * Generate UPI QR Code
   */
  async generateUPIQRCode(upiId, amount, name, invoiceNo = '') {
    if (!upiId) return null;
    
    try {
      const cleanUpiId = upiId.trim().toLowerCase();
      const params = new URLSearchParams({
        pa: cleanUpiId,
        pn: name.substring(0, 25),
        am: amount.toFixed(2),
        tn: `Inv ${invoiceNo}`,
        cu: 'INR'
      });
      
      const upiUrl = `upi://pay?${params.toString()}`;
      
      const qrDataUrl = await QRCode.toDataURL(upiUrl, {
        errorCorrectionLevel: 'H',
        margin: 2,
        width: 150,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      return qrDataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  }

  /**
   * Main print method
   */
  async print(data) {
    try {
      const { settings, company, selectedCustomer, invoiceItems, invoiceNo, invoiceDate, dueDate, notes, terms, bankDetails, upiDetails, invoiceType = 'tax-invoice' } = data;
      
      const paperSize = this.paperSizes[settings.billSize] || this.paperSizes.a4;
      const billFormat = this.billFormats[settings.billFormat] || this.billFormats['tax-invoice'];
      const totals = this.calculateTotals(invoiceItems);
      
      // Generate QR code
      let qrCodeDataUrl = null;
      if (upiDetails?.upiId && upiDetails.showQR !== false) {
        qrCodeDataUrl = await this.generateUPIQRCode(
          upiDetails.upiId,
          totals.finalTotal,
          company.name,
          invoiceNo
        );
      }
      
      // Get template based on paper size
      const content = paperSize.thermal 
        ? this.renderThermalInvoice({ ...data, totals, qrCodeDataUrl, billFormat })
        : this.renderStandardInvoice({ ...data, totals, qrCodeDataUrl, billFormat });
      
      const html = this.generatePrintHTML(content, paperSize, settings);
      return this.printWithContainer(html);
      
    } catch (error) {
      console.error('Print error:', error);
      throw new Error(`Failed to print: ${error.message}`);
    }
  }

  /**
   * Render Standard Professional Invoice (A4/Letter/A5)
   */
  renderStandardInvoice(data) {
    const { company, selectedCustomer, invoiceItems, totals, invoiceNo, invoiceDate, dueDate, notes, terms, billFormat, qrCodeDataUrl, bankDetails, upiDetails, paymentMode } = data;
    
    const isIntraState = selectedCustomer?.state?.toLowerCase() === company.state?.toLowerCase();
    const gstType = isIntraState ? 'CGST + SGST' : 'IGST';
    
    return `
      <div class="invoice-wrapper">
        <!-- Main Invoice Container -->
        <div class="invoice-container">
          
          <!-- HEADER SECTION -->
          <div class="header-section">
            <table class="header-table" width="100%">
              <tr>
                <td width="60%" class="company-details">
                  <div class="company-name">${this.escapeHtml(company.name || 'YOUR COMPANY NAME')}</div>
                  <div class="company-address">${this.escapeHtml(company.address || '')}</div>
                  <div class="company-contact">
                    ${company.phone ? `📞 ${company.phone}` : ''}
                    ${company.email ? ` | ✉️ ${company.email}` : ''}
                    ${company.website ? ` | 🌐 ${company.website}` : ''}
                  </div>
                  <div class="company-gst">
                    <strong>GSTIN/UIN:</strong> ${company.gstin || '___________________'}
                    ${company.pan ? ` | <strong>PAN:</strong> ${company.pan}` : ''}
                    ${company.cin ? ` | <strong>CIN:</strong> ${company.cin}` : ''}
                  </div>
                </td>
                <td width="40%" class="invoice-title-section">
                  <div class="invoice-title">${billFormat.title}</div>
                  <div class="invoice-subtitle">${billFormat.subtitle}</div>
                  <div class="invoice-type">${gstType}</div>
                </td>
              </tr>
            </table>
          </div>

          <!-- INVOICE DETAILS SECTION -->
          <div class="details-section">
            <table class="details-table" width="100%">
              <tr>
                <td width="50%" class="invoice-details-box">
                  <table width="100%">
                    <tr>
                      <td width="40%" class="label">Invoice No.</td>
                      <td width="60%" class="value"><strong>${invoiceNo}</strong></td>
                    </tr>
                    <tr>
                      <td class="label">Invoice Date</td>
                      <td class="value">${this.formatDate(invoiceDate)}</td>
                    </tr>
                    ${dueDate ? `
                    <tr>
                      <td class="label">Due Date</td>
                      <td class="value">${this.formatDate(dueDate)}</td>
                    </tr>
                    ` : ''}
                    <tr>
                      <td class="label">Order No.</td>
                      <td class="value">${terms?.orderNo || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td class="label">Order Date</td>
                      <td class="value">${terms?.orderDate ? this.formatDate(terms.orderDate) : 'N/A'}</td>
                    </tr>
                  </table>
                </td>
                <td width="50%" class="delivery-details-box">
                  <table width="100%">
                    <tr>
                      <td width="40%" class="label">Place of Supply</td>
                      <td width="60%" class="value">${selectedCustomer?.state || 'N/A'} (Code: ${selectedCustomer?.stateCode || '-'})</td>
                    </tr>
                    <tr>
                      <td class="label">Payment Mode</td>
                      <td class="value">${this.getPaymentModeName(paymentMode)}</td>
                    </tr>
                    <tr>
                      <td class="label">Delivery Note</td>
                      <td class="value">${terms?.deliveryNote || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td class="label">Vehicle No.</td>
                      <td class="value">${terms?.vehicleNo || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td class="label">E-Way Bill No.</td>
                      <td class="value">${terms?.ewayBillNo || 'N/A'}</td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </div>

          <!-- PARTY DETAILS SECTION -->
          <div class="party-section">
            <table class="party-table" width="100%">
              <tr>
                <td class="party-title" colspan="2">BILL TO:</td>
                <td class="party-title" colspan="2">SHIP TO:</td>
              </tr>
              <tr>
                <td width="5%" class="label">Name</td>
                <td width="45%" class="value"><strong>${this.escapeHtml(selectedCustomer.name)}</strong></td>
                <td width="5%" class="label">Name</td>
                <td width="45%" class="value"><strong>${this.escapeHtml(selectedCustomer.name)}</strong></td>
              </tr>
              <tr>
                <td class="label">Address</td>
                <td class="value">${this.escapeHtml(selectedCustomer.address || '')}</td>
                <td class="label">Address</td>
                <td class="value">${this.escapeHtml(selectedCustomer.address || '')}</td>
              </tr>
              <tr>
                <td class="label">GSTIN</td>
                <td class="value">${selectedCustomer.gst || 'Unregistered'}</td>
                <td class="label">State</td>
                <td class="value">${selectedCustomer.state || ''} (${selectedCustomer.stateCode || ''})</td>
              </tr>
              <tr>
                <td class="label">Mobile</td>
                <td class="value">${selectedCustomer.mobile || ''}</td>
                <td class="label">Email</td>
                <td class="value">${selectedCustomer.email || ''}</td>
              </tr>
              ${selectedCustomer.pan ? `
              <tr>
                <td class="label">PAN</td>
                <td class="value">${selectedCustomer.pan}</td>
                <td class="label"></td>
                <td class="value"></td>
              </tr>
              ` : ''}
            </table>
          </div>

          <!-- ITEMS TABLE SECTION -->
          <div class="items-section">
            <table class="items-table" width="100%">
              <thead>
                <tr>
                  <th width="5%">Sl.</th>
                  <th width="25%">Description of Goods</th>
                  <th width="8%">HSN/SAC</th>
                  <th width="7%">Qty</th>
                  <th width="7%">Unit</th>
                  <th width="10%">Rate (₹)</th>
                  <th width="7%">Disc %</th>
                  <th width="10%">Taxable (₹)</th>
                  <th width="6%">GST %</th>
                  ${isIntraState ? 
                    '<th width="7%">CGST (₹)</th><th width="7%">SGST (₹)</th>' : 
                    '<th width="10%">IGST (₹)</th>'
                  }
                  <th width="10%">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                ${invoiceItems.map((item, index) => `
                  <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>${this.escapeHtml(item.name)}</td>
                    <td class="text-center">${item.hsn || '-'}</td>
                    <td class="text-right">${this.formatNumber(item.qty)}</td>
                    <td class="text-center">${item.unit || 'NOS'}</td>
                    <td class="text-right">${this.formatCurrency(item.price)}</td>
                    <td class="text-right">${item.discount || 0}%</td>
                    <td class="text-right">${this.formatCurrency(item.taxableValue)}</td>
                    <td class="text-center">${item.gst || 0}%</td>
                    ${isIntraState ? 
                      `<td class="text-right">${this.formatCurrency(item.gstDetails?.cgst)}</td>
                       <td class="text-right">${this.formatCurrency(item.gstDetails?.sgst)}</td>` : 
                      `<td class="text-right">${this.formatCurrency(item.gstDetails?.igst)}</td>`
                    }
                    <td class="text-right"><strong>${this.formatCurrency(item.netTotal)}</strong></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <!-- TOTALS SECTION -->
          <div class="totals-section">
            <table class="totals-table" width="100%">
              <tr>
                <td width="50%" class="amount-words">
                  <div class="words-box">
                    <div class="words-title">Amount Chargeable (in words):</div>
                    <div class="words-value"><strong>${this.numberToWords(totals.finalTotal)}</strong></div>
                  </div>
                  
                  ${bankDetails && bankDetails.bankName ? `
                  <div class="bank-details-box">
                    <div class="bank-title">Bank Details:</div>
                    <div class="bank-content">
                      <strong>${bankDetails.bankName}</strong><br>
                      A/c No.: ${bankDetails.accountNo ? 'XXXX' + bankDetails.accountNo.slice(-4) : 'N/A'}<br>
                      IFSC: ${bankDetails.ifsc || 'N/A'}<br>
                      Branch: ${bankDetails.branch || 'N/A'}<br>
                      Holder: ${bankDetails.holderName || 'N/A'}
                    </div>
                  </div>
                  ` : ''}
                </td>
                <td width="50%">
                  <table class="summary-table" width="100%">
                    <tr>
                      <td class="summary-label">Subtotal</td>
                      <td class="summary-value">${this.formatCurrency(totals.subtotal)}</td>
                    </tr>
                    <tr>
                      <td class="summary-label">Total Discount</td>
                      <td class="summary-value">${this.formatCurrency(totals.totalDiscount)}</td>
                    </tr>
                    <tr>
                      <td class="summary-label">Total Taxable Value</td>
                      <td class="summary-value">${this.formatCurrency(totals.totalTaxable)}</td>
                    </tr>
                    ${totals.totalCgst > 0 ? `
                    <tr>
                      <td class="summary-label">CGST</td>
                      <td class="summary-value">${this.formatCurrency(totals.totalCgst)}</td>
                    </tr>
                    ` : ''}
                    ${totals.totalSgst > 0 ? `
                    <tr>
                      <td class="summary-label">SGST</td>
                      <td class="summary-value">${this.formatCurrency(totals.totalSgst)}</td>
                    </tr>
                    ` : ''}
                    ${totals.totalIgst > 0 ? `
                    <tr>
                      <td class="summary-label">IGST</td>
                      <td class="summary-value">${this.formatCurrency(totals.totalIgst)}</td>
                    </tr>
                    ` : ''}
                    <tr class="total-row">
                      <td class="summary-label"><strong>Grand Total</strong></td>
                      <td class="summary-value"><strong>${this.formatCurrency(totals.finalTotal)}</strong></td>
                    </tr>
                    ${totals.roundOff !== 0 ? `
                    <tr>
                      <td class="summary-label">Round Off</td>
                      <td class="summary-value">${this.formatCurrency(totals.roundOff)}</td>
                    </tr>
                    ` : ''}
                  </table>
                </td>
              </tr>
            </table>
          </div>

          <!-- GST SUMMARY SECTION (if applicable) -->
          ${billFormat.showGstDetails && totals.gstSummary?.length > 0 ? `
          <div class="gst-summary-section">
            <div class="gst-title">GST Summary</div>
            <table class="gst-summary-table" width="100%">
              <thead>
                <tr>
                  <th>HSN/SAC</th>
                  <th>Taxable Value</th>
                  <th>CGST %</th>
                  <th>CGST Amount</th>
                  <th>SGST %</th>
                  <th>SGST Amount</th>
                  <th>IGST %</th>
                  <th>IGST Amount</th>
                  <th>Total Tax</th>
                </tr>
              </thead>
              <tbody>
                ${this.generateGstSummary(invoiceItems).map(item => `
                  <tr>
                    <td class="text-center">${item.hsn}</td>
                    <td class="text-right">${this.formatCurrency(item.taxableValue)}</td>
                    <td class="text-center">${item.cgstRate}%</td>
                    <td class="text-right">${this.formatCurrency(item.cgstAmount)}</td>
                    <td class="text-center">${item.sgstRate}%</td>
                    <td class="text-right">${this.formatCurrency(item.sgstAmount)}</td>
                    <td class="text-center">${item.igstRate}%</td>
                    <td class="text-right">${this.formatCurrency(item.igstAmount)}</td>
                    <td class="text-right">${this.formatCurrency(item.totalTax)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          <!-- QR CODE SECTION -->
          ${qrCodeDataUrl ? `
          <div class="qr-section">
            <table class="qr-table" width="100%">
              <tr>
                <td class="qr-content">
                  <div class="qr-wrapper">
                    <img src="${qrCodeDataUrl}" alt="UPI QR Code" class="qr-code">
                    <div class="upi-details">
                      <strong>UPI ID:</strong> ${upiDetails?.upiId}<br>
                      <strong>Scan to Pay:</strong> ₹${totals.finalTotal.toFixed(2)}
                    </div>
                  </div>
                </td>
              </tr>
            </table>
          </div>
          ` : upiDetails?.upiId ? `
          <div class="upi-section">
            <div class="upi-info">
              <strong>UPI ID:</strong> ${upiDetails.upiId}
            </div>
          </div>
          ` : ''}

          <!-- TERMS & CONDITIONS SECTION -->
          <div class="terms-section">
            <div class="terms-title">Terms & Conditions:</div>
            <div class="terms-content">
              ${this.escapeHtml(terms || '1. Goods once sold will not be taken back or exchanged.\n2. Interest @ 24% p.a. will be charged if payment is not made within due date.\n3. Subject to [City] jurisdiction.\n4. E. & O.E.')}
            </div>
          </div>

          <!-- NOTES SECTION -->
          ${notes ? `
          <div class="notes-section">
            <div class="notes-title">Notes:</div>
            <div class="notes-content">${this.escapeHtml(notes)}</div>
          </div>
          ` : ''}

          <!-- SIGNATURE SECTION -->
          <div class="signature-section">
            <table class="signature-table" width="100%">
              <tr>
                <td width="50%" class="signature-left">
                  <div>Receiver's Signature</div>
                  <div class="signature-line"></div>
                </td>
                <td width="50%" class="signature-right">
                  <div>For ${this.escapeHtml(company.name)}</div>
                  <div class="signature-line"></div>
                  <div class="authorised-sign">Authorised Signatory</div>
                </td>
              </tr>
            </table>
          </div>

          <!-- FOOTER SECTION -->
          <div class="footer-section">
            <div class="footer-text">
              This is a computer generated invoice - valid without signature
            </div>
            <div class="footer-date">
              Generated on: ${new Date().toLocaleString()}
            </div>
          </div>
        </div>

        <style>
          /* Professional Invoice Styles */
          .invoice-container {
            max-width: 100%;
            margin: 0 auto;
            font-family: 'Arial', 'Helvetica', sans-serif;
            color: #000000;
          }
          
          /* Header Styles */
          .header-section {
            margin-bottom: 20px;
            border-bottom: 2px solid #000000;
            padding-bottom: 15px;
          }
          
          .company-name {
            font-size: 22px;
            font-weight: bold;
            text-transform: uppercase;
            color: #000000;
            margin-bottom: 5px;
          }
          
          .company-address {
            font-size: 9pt;
            color: #333333;
            margin-bottom: 3px;
          }
          
          .company-contact {
            font-size: 9pt;
            color: #333333;
            margin-bottom: 3px;
          }
          
          .company-gst {
            font-size: 9pt;
            color: #333333;
            margin-top: 5px;
          }
          
          .invoice-title-section {
            text-align: right;
            border-left: 2px solid #000000;
            padding-left: 15px;
          }
          
          .invoice-title {
            font-size: 20px;
            font-weight: bold;
            color: #000000;
            text-transform: uppercase;
          }
          
          .invoice-subtitle {
            font-size: 9pt;
            color: #666666;
            margin-top: 3px;
          }
          
          .invoice-type {
            font-size: 10pt;
            font-weight: bold;
            color: #5a60ea;
            margin-top: 5px;
          }
          
          /* Details Section */
          .details-section {
            margin-bottom: 15px;
          }
          
          .details-table, .party-table, .items-table, .totals-table {
            border-collapse: collapse;
            width: 100%;
          }
          
          .invoice-details-box, .delivery-details-box {
            border: 1px solid #000000;
            padding: 8px;
          }
          
          .label {
            font-weight: normal;
            color: #555555;
            padding: 4px;
          }
          
          .value {
            font-weight: normal;
            color: #000000;
            padding: 4px;
          }
          
          /* Party Section */
          .party-section {
            margin-bottom: 15px;
          }
          
          .party-table {
            border: 1px solid #000000;
            border-collapse: collapse;
          }
          
          .party-table td {
            border: 1px solid #000000;
            padding: 6px;
            vertical-align: top;
          }
          
          .party-title {
            background-color: #f0f0f0;
            font-weight: bold;
            font-size: 10pt;
          }
          
          /* Items Table */
          .items-section {
            margin-bottom: 15px;
            overflow-x: auto;
          }
          
          .items-table {
            border-collapse: collapse;
            width: 100%;
            font-size: 9pt;
          }
          
          .items-table th {
            background-color: #e0e0e0;
            border: 1px solid #000000;
            padding: 8px;
            font-weight: bold;
            text-align: center;
          }
          
          .items-table td {
            border: 1px solid #000000;
            padding: 6px;
          }
          
          .text-center {
            text-align: center;
          }
          
          .text-right {
            text-align: right;
          }
          
          /* Totals Section */
          .totals-section {
            margin-bottom: 15px;
          }
          
          .amount-words {
            vertical-align: top;
          }
          
          .words-box {
            border: 1px solid #000000;
            padding: 10px;
            margin-bottom: 10px;
          }
          
          .words-title {
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .words-value {
            font-size: 11pt;
          }
          
          .bank-details-box {
            border: 1px solid #000000;
            padding: 10px;
          }
          
          .bank-title {
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .bank-content {
            font-size: 8pt;
            line-height: 1.4;
          }
          
          .summary-table {
            border-collapse: collapse;
            width: 100%;
          }
          
          .summary-table td {
            border: 1px solid #000000;
            padding: 6px;
          }
          
          .summary-label {
            font-weight: normal;
          }
          
          .summary-value {
            text-align: right;
            font-weight: normal;
          }
          
          .total-row {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          
          /* GST Summary */
          .gst-summary-section {
            margin-bottom: 15px;
          }
          
          .gst-title {
            font-weight: bold;
            margin-bottom: 8px;
            font-size: 11pt;
          }
          
          .gst-summary-table {
            border-collapse: collapse;
            width: 100%;
            font-size: 8pt;
          }
          
          .gst-summary-table th {
            background-color: #e0e0e0;
            border: 1px solid #000000;
            padding: 6px;
          }
          
          .gst-summary-table td {
            border: 1px solid #000000;
            padding: 6px;
          }
          
          /* QR Section */
          .qr-section {
            margin-bottom: 15px;
            text-align: center;
          }
          
          .qr-wrapper {
            display: inline-block;
            text-align: center;
          }
          
          .qr-code {
            width: 120px;
            height: 120px;
            margin-bottom: 5px;
          }
          
          .upi-details {
            font-size: 9pt;
          }
          
          .upi-section {
            margin-bottom: 15px;
            text-align: center;
            border: 1px solid #000000;
            padding: 10px;
          }
          
          /* Terms & Notes */
          .terms-section, .notes-section {
            margin-bottom: 15px;
            border: 1px solid #000000;
            padding: 10px;
          }
          
          .terms-title, .notes-title {
            font-weight: bold;
            margin-bottom: 5px;
          }
          
          .terms-content, .notes-content {
            font-size: 8pt;
            line-height: 1.4;
            white-space: pre-line;
          }
          
          /* Signature Section */
          .signature-section {
            margin-top: 30px;
            margin-bottom: 15px;
          }
          
          .signature-line {
            border-top: 1px solid #000000;
            width: 200px;
            margin-top: 30px;
          }
          
          .signature-left .signature-line {
            margin-top: 30px;
          }
          
          .signature-right {
            text-align: right;
          }
          
          .signature-right .signature-line {
            float: right;
          }
          
          .authorised-sign {
            clear: both;
            margin-top: 5px;
          }
          
          /* Footer */
          .footer-section {
            text-align: center;
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px dashed #999999;
          }
          
          .footer-text {
            font-size: 8pt;
            color: #666666;
            margin-bottom: 5px;
          }
          
          .footer-date {
            font-size: 7pt;
            color: #999999;
          }
          
          /* Print Styles */
          @media print {
            body {
              margin: 0;
              padding: 0;
            }
            
            .invoice-container {
              margin: 0;
              padding: 0;
            }
            
            .qr-code {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
          
          /* Responsive */
          @media (max-width: 768px) {
            .items-table {
              font-size: 8pt;
            }
            
            .items-table th, .items-table td {
              padding: 4px;
            }
          }
        </style>
      </div>
    `;
  }

  /**
   * Render Thermal Invoice (80mm/58mm)
   */
  renderThermalInvoice(data) {
    const { company, selectedCustomer, invoiceItems, totals, invoiceNo, invoiceDate, billFormat, qrCodeDataUrl, upiDetails } = data;
    
    return `
      <div class="thermal-invoice">
        <div class="thermal-container">
          <!-- Header -->
          <div class="thermal-header">
            <div class="thermal-company">${this.escapeHtml(company.name || 'YOUR COMPANY')}</div>
            <div class="thermal-address">${this.escapeHtml(company.address || '')}</div>
            <div class="thermal-contact">Ph: ${company.phone || ''}</div>
            <div class="thermal-gst">GST: ${company.gstin || ''}</div>
            <div class="thermal-divider">${'='.repeat(42)}</div>
            <div class="thermal-title">${billFormat.title}</div>
            <div class="thermal-divider">${'='.repeat(42)}</div>
          </div>

          <!-- Invoice Details -->
          <div class="thermal-details">
            <div>Inv No: ${invoiceNo}</div>
            <div>Date: ${this.formatDate(invoiceDate, true)}</div>
            <div>Customer: ${this.escapeHtml(selectedCustomer.name.substring(0, 25))}</div>
            ${selectedCustomer.gst ? `<div>GST: ${selectedCustomer.gst}</div>` : ''}
            <div class="thermal-divider">${'-'.repeat(42)}</div>
          </div>

          <!-- Items -->
          <table class="thermal-items">
            <thead>
              <tr>
                <th style="text-align: left;">Item</th>
                <th style="text-align: right;">Qty</th>
                <th style="text-align: right;">Rate</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoiceItems.map(item => `
                <tr>
                  <td style="text-align: left;">${this.escapeHtml(item.name.substring(0, 15))}</td>
                  <td style="text-align: right;">${this.formatNumber(item.qty)}</td>
                  <td style="text-align: right;">${this.formatCurrency(item.price)}</td>
                  <td style="text-align: right;">${this.formatCurrency(item.netTotal)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="thermal-divider">${'-'.repeat(42)}</div>

          <!-- Totals -->
          <div class="thermal-totals">
            <div class="thermal-line">
              <span>Subtotal:</span>
              <span>${this.formatCurrency(totals.subtotal)}</span>
            </div>
            <div class="thermal-line">
              <span>Discount:</span>
              <span>${this.formatCurrency(totals.totalDiscount)}</span>
            </div>
            <div class="thermal-line">
              <span>Taxable:</span>
              <span>${this.formatCurrency(totals.totalTaxable)}</span>
            </div>
            ${totals.totalCgst > 0 ? `
            <div class="thermal-line">
              <span>CGST:</span>
              <span>${this.formatCurrency(totals.totalCgst)}</span>
            </div>
            ` : ''}
            ${totals.totalSgst > 0 ? `
            <div class="thermal-line">
              <span>SGST:</span>
              <span>${this.formatCurrency(totals.totalSgst)}</span>
            </div>
            ` : ''}
            ${totals.totalIgst > 0 ? `
            <div class="thermal-line">
              <span>IGST:</span>
              <span>${this.formatCurrency(totals.totalIgst)}</span>
            </div>
            ` : ''}
            <div class="thermal-divider">${'='.repeat(42)}</div>
            <div class="thermal-total">
              <strong>TOTAL: ${this.formatCurrency(totals.finalTotal)}</strong>
            </div>
          </div>

          <!-- QR Code -->
          ${qrCodeDataUrl ? `
          <div class="thermal-qr">
            <img src="${qrCodeDataUrl}" alt="QR" style="width: 80px; height: 80px;">
            <div>UPI: ${upiDetails?.upiId}</div>
            <div>Scan to pay</div>
          </div>
          ` : ''}

          <!-- Footer -->
          <div class="thermal-footer">
            <div class="thermal-divider">${'='.repeat(42)}</div>
            <div>Thank you! Visit again</div>
            <div>${new Date().toLocaleString()}</div>
          </div>
        </div>

        <style>
          .thermal-invoice {
            font-family: 'Courier New', monospace;
            width: 100%;
            margin: 0 auto;
          }
          
          .thermal-container {
            width: 100%;
          }
          
          .thermal-header {
            text-align: center;
            margin-bottom: 8px;
          }
          
          .thermal-company {
            font-size: 12pt;
            font-weight: bold;
            text-transform: uppercase;
          }
          
          .thermal-address, .thermal-contact, .thermal-gst {
            font-size: 7pt;
            margin: 2px 0;
          }
          
          .thermal-title {
            font-size: 10pt;
            font-weight: bold;
            margin: 5px 0;
          }
          
          .thermal-divider {
            font-size: 7pt;
            margin: 4px 0;
          }
          
          .thermal-details {
            font-size: 7pt;
            margin-bottom: 8px;
          }
          
          .thermal-items {
            width: 100%;
            font-size: 7pt;
            border-collapse: collapse;
            margin-bottom: 8px;
          }
          
          .thermal-items th, .thermal-items td {
            padding: 2px 0;
          }
          
          .thermal-totals {
            font-size: 7pt;
            margin: 8px 0;
          }
          
          .thermal-line {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
          }
          
          .thermal-total {
            font-size: 9pt;
            font-weight: bold;
            text-align: right;
            margin: 5px 0;
          }
          
          .thermal-qr {
            text-align: center;
            margin: 10px 0;
          }
          
          .thermal-footer {
            text-align: center;
            font-size: 6pt;
            margin-top: 10px;
          }
          
          @media print {
            .thermal-invoice {
              margin: 0;
              padding: 0;
            }
          }
        </style>
      </div>
    `;
  }

  /**
   * Generate GST Summary by HSN
   */
  generateGstSummary(items) {
    const summary = {};
    
    items.forEach(item => {
      const hsn = item.hsn || 'N/A';
      if (!summary[hsn]) {
        summary[hsn] = {
          hsn: hsn,
          taxableValue: 0,
          cgstRate: item.cgstRate || 0,
          sgstRate: item.sgstRate || 0,
          igstRate: item.igstRate || 0,
          cgstAmount: 0,
          sgstAmount: 0,
          igstAmount: 0,
          totalTax: 0
        };
      }
      
      summary[hsn].taxableValue += item.taxableValue || 0;
      summary[hsn].cgstAmount += item.gstDetails?.cgst || 0;
      summary[hsn].sgstAmount += item.gstDetails?.sgst || 0;
      summary[hsn].igstAmount += item.gstDetails?.igst || 0;
      summary[hsn].totalTax += item.gstDetails?.total || 0;
    });
    
    return Object.values(summary);
  }

  /**
   * Calculate invoice totals with proper GST breakdown
   */
  calculateTotals(items) {
    let subtotal = 0;
    let totalTaxable = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    let totalGst = 0;
    let totalDiscount = 0;
    let grandTotal = 0;

    items.forEach(item => {
      subtotal += item.itemTotal || 0;
      totalTaxable += item.taxableValue || 0;
      totalCgst += item.gstDetails?.cgst || 0;
      totalSgst += item.gstDetails?.sgst || 0;
      totalIgst += item.gstDetails?.igst || 0;
      totalGst += item.gstDetails?.total || 0;
      totalDiscount += item.discountAmount || 0;
      grandTotal += item.netTotal || 0;
    });

    const finalTotal = Math.round(grandTotal);
    const roundOff = finalTotal - grandTotal;
    
    const gstSummary = this.generateGstSummary(items);

    return {
      subtotal,
      totalTaxable,
      totalCgst,
      totalSgst,
      totalIgst,
      totalGst,
      totalDiscount,
      grandTotal,
      finalTotal,
      roundOff,
      gstSummary
    };
  }

  /**
   * Print with hidden container
   */
  printWithContainer(html) {
    return new Promise((resolve, reject) => {
      try {
        // Remove any existing print container
        const existingContainer = document.getElementById('print-container');
        if (existingContainer) {
          existingContainer.remove();
        }
        
        const printContainer = document.createElement('div');
        printContainer.id = 'print-container';
        printContainer.style.position = 'fixed';
        printContainer.style.top = '-10000px';
        printContainer.style.left = '-10000px';
        printContainer.style.width = '100%';
        printContainer.style.backgroundColor = 'white';
        printContainer.style.zIndex = '-1';
        
        printContainer.innerHTML = html;
        document.body.appendChild(printContainer);
        
        // Wait for images to load
        const images = printContainer.getElementsByTagName('img');
        let imagesToLoad = images.length;
        
        if (imagesToLoad === 0) {
          this.executePrint(printContainer, resolve, reject);
        } else {
          let loadedImages = 0;
          
          const imageLoaded = () => {
            loadedImages++;
            if (loadedImages === imagesToLoad) {
              this.executePrint(printContainer, resolve, reject);
            }
          };
          
          for (let img of images) {
            if (img.complete) {
              imageLoaded();
            } else {
              img.onload = imageLoaded;
              img.onerror = imageLoaded;
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Execute print
   */
  executePrint(printContainer, resolve, reject) {
    try {
      printContainer.style.position = 'relative';
      printContainer.style.top = '0';
      printContainer.style.left = '0';
      printContainer.style.zIndex = '999999';
      
      printContainer.scrollIntoView({ behavior: 'instant', block: 'start' });
      
      window.print();
      
      const cleanup = () => {
        setTimeout(() => {
          if (printContainer && printContainer.parentNode) {
            printContainer.parentNode.removeChild(printContainer);
          }
          document.body.style.overflow = '';
          window.removeEventListener('afterprint', cleanup);
          resolve(true);
        }, 500);
      };
      
      window.addEventListener('afterprint', cleanup);
      
      setTimeout(() => {
        if (printContainer && printContainer.parentNode) {
          printContainer.parentNode.removeChild(printContainer);
        }
        resolve(true);
      }, 10000);
      
    } catch (error) {
      reject(error);
    }
  }

  /**
   * Generate print HTML
   */
  generatePrintHTML(content, paperSize, settings) {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${settings.billFormat === 'proforma' ? 'Proforma Invoice' : 'Tax Invoice'}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0, shrink-to-fit=no">
          ${paperSize.css}
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              margin: 0;
              padding: 0;
              background: white;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              
              .no-print {
                display: none;
              }
              
              .page-break {
                page-break-before: always;
              }
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;
  }

  /**
   * Get payment mode name
   */
  getPaymentModeName(mode) {
    const modes = {
      cash: 'Cash',
      bank: 'Bank Transfer',
      cheque: 'Cheque',
      upi: 'UPI',
      card: 'Card Payment'
    };
    return modes[mode] || mode;
  }

  /**
   * Format currency
   */
  formatCurrency(amount) {
    if (amount === undefined || amount === null) return '0.00';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Format number
   */
  formatNumber(num) {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(num);
  }

  /**
   * Format date
   */
  formatDate(date, short = false) {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (short) {
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
    return d.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  }

  /**
   * Escape HTML
   */
  escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Convert number to words (Indian Rupees)
   */
  numberToWords(num) {
    if (num === 0) return 'Zero Rupees Only';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      
      let str = '';
      
      if (n >= 100) {
        str += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      
      if (n >= 20) {
        str += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        str += teens[n - 10] + ' ';
        n = 0;
      }
      
      if (n > 0) {
        str += ones[n] + ' ';
      }
      
      return str;
    };

    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    
    let result = '';
    
    const crore = Math.floor(rupees / 10000000);
    const lakh = Math.floor((rupees % 10000000) / 100000);
    const thousand = Math.floor((rupees % 100000) / 1000);
    const hundred = rupees % 1000;
    
    if (crore > 0) {
      result += convertLessThanThousand(crore) + 'Crore ';
    }
    
    if (lakh > 0) {
      result += convertLessThanThousand(lakh) + 'Lakh ';
    }
    
    if (thousand > 0) {
      result += convertLessThanThousand(thousand) + 'Thousand ';
    }
    
    if (hundred > 0) {
      result += convertLessThanThousand(hundred);
    }
    
    result = result.trim() + ' Rupees';
    
    if (paise > 0) {
      result += ' and ' + convertLessThanThousand(paise).trim() + ' Paise';
    }
    
    return result + ' Only';
  }
}

export default PrintService;